export const PI = Math.PI;
export const TWO_PI = Math.PI * 2;
export const HALF_PI = Math.PI / 2;
export const DEG = Math.PI / 180;
export const MAX_PITCH = HALF_PI - 0.02;

export interface Vec3 {
  x: number;
  y: number;
  z: number;
}

export const vec3 = (x = 0, y = 0, z = 0): Vec3 => ({ x, y, z });

export function clamp(v: number, lo: number, hi: number): number {
  return v < lo ? lo : v > hi ? hi : v;
}

export function lerp(a: number, b: number, t: number): number {
  return a + (b - a) * t;
}

export function approach(cur: number, target: number, delta: number): number {
  if (cur < target) return Math.min(cur + delta, target);
  return Math.max(cur - delta, target);
}

export function wrapAngle(a: number): number {
  a = a % TWO_PI;
  if (a < 0) a += TWO_PI;
  return a;
}

/** Shortest signed difference b - a in (-PI, PI]. */
export function angleDiff(a: number, b: number): number {
  let d = (b - a) % TWO_PI;
  if (d > PI) d -= TWO_PI;
  else if (d <= -PI) d += TWO_PI;
  return d;
}

export function lerpAngle(a: number, b: number, t: number): number {
  return a + angleDiff(a, b) * t;
}

export function dist3(ax: number, ay: number, az: number, bx: number, by: number, bz: number): number {
  const dx = ax - bx;
  const dy = ay - by;
  const dz = az - bz;
  return Math.sqrt(dx * dx + dy * dy + dz * dz);
}

/** Forward unit vector for a yaw/pitch pair. Yaw 0 looks toward -Z; positive yaw turns left (counter-clockwise from above). */
export function forwardFromAngles(yaw: number, pitch: number, out: Vec3): Vec3 {
  const cp = Math.cos(pitch);
  out.x = -Math.sin(yaw) * cp;
  out.y = Math.sin(pitch);
  out.z = -Math.cos(yaw) * cp;
  return out;
}

export function anglesFromDirection(dx: number, dy: number, dz: number): { yaw: number; pitch: number } {
  const h = Math.sqrt(dx * dx + dz * dz);
  return { yaw: wrapAngle(Math.atan2(-dx, -dz)), pitch: Math.atan2(dy, h) };
}

// Quantization ------------------------------------------------------------------

export function quantizeYaw(yaw: number): number {
  return Math.round((wrapAngle(yaw) / TWO_PI) * 65536) & 0xffff;
}
export function dequantizeYaw(q: number): number {
  return (q / 65536) * TWO_PI;
}
export function quantizePitch(pitch: number): number {
  return clamp(Math.round((pitch / HALF_PI) * 32767), -32767, 32767);
}
export function dequantizePitch(q: number): number {
  return (q / 32767) * HALF_PI;
}
export function quantizeAxis(v: number): number {
  return clamp(Math.round(v * 127), -127, 127);
}
export function dequantizeAxis(q: number): number {
  return q / 127;
}

// Deterministic randomness -------------------------------------------------------

/** mulberry32: small, fast, and bit-identical on every JS engine. */
export class Rng {
  s: number;
  constructor(seed: number) {
    this.s = seed >>> 0;
  }
  u32(): number {
    let t = (this.s = (this.s + 0x6d2b79f5) >>> 0);
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return (t ^ (t >>> 14)) >>> 0;
  }
  /** [0, 1) */
  next(): number {
    return this.u32() / 4294967296;
  }
  /** Integer in [min, max] inclusive. */
  int(min: number, max: number): number {
    return min + Math.floor(this.next() * (max - min + 1));
  }
  range(min: number, max: number): number {
    return min + this.next() * (max - min);
  }
  chance(p: number): boolean {
    return this.next() < p;
  }
  pick<T>(arr: readonly T[]): T {
    return arr[Math.floor(this.next() * arr.length)];
  }
  shuffle<T>(arr: T[]): T[] {
    for (let i = arr.length - 1; i > 0; i--) {
      const j = Math.floor(this.next() * (i + 1));
      const t = arr[i];
      arr[i] = arr[j];
      arr[j] = t;
    }
    return arr;
  }
}

export function hash32(a: number, b = 0, c = 0, d = 0): number {
  let h = Math.imul(a | 0, 0x27d4eb2d) ^ Math.imul(b | 0, 0x165667b1) ^ Math.imul(c | 0, 0x9e3779b1) ^ Math.imul(d | 0, 0x85ebca77);
  h = Math.imul(h ^ (h >>> 15), 0x2c1b3c6d);
  h = Math.imul(h ^ (h >>> 12), 0x297a2d39);
  return (h ^ (h >>> 15)) >>> 0;
}

export function hashString(s: string): number {
  let h = 0x811c9dc5;
  for (let i = 0; i < s.length; i++) {
    h ^= s.charCodeAt(i);
    h = Math.imul(h, 0x01000193);
  }
  return h >>> 0;
}

export function fnv1a(data: Uint8Array, h = 0x811c9dc5): number {
  for (let i = 0; i < data.length; i++) {
    h ^= data[i];
    h = Math.imul(h, 0x01000193);
  }
  return h >>> 0;
}

// Deterministic trig for content generation (Math.sin is not bit-identical across engines).

export function dsin(x: number): number {
  x = x - TWO_PI * Math.floor((x + PI) / TWO_PI);
  if (x > HALF_PI) x = PI - x;
  else if (x < -HALF_PI) x = -PI - x;
  const x2 = x * x;
  return x * (1 - (x2 / 6) * (1 - (x2 / 20) * (1 - (x2 / 42) * (1 - (x2 / 72) * (1 - x2 / 110)))));
}

export function dcos(x: number): number {
  return dsin(x + HALF_PI);
}

/** Deterministic 2D value noise in [0, 1). */
export function valueNoise2(x: number, z: number, seed: number): number {
  const x0 = Math.floor(x);
  const z0 = Math.floor(z);
  const fx = x - x0;
  const fz = z - z0;
  const sx = fx * fx * (3 - 2 * fx);
  const sz = fz * fz * (3 - 2 * fz);
  const n00 = hash32(x0, z0, seed) / 4294967296;
  const n10 = hash32(x0 + 1, z0, seed) / 4294967296;
  const n01 = hash32(x0, z0 + 1, seed) / 4294967296;
  const n11 = hash32(x0 + 1, z0 + 1, seed) / 4294967296;
  const a = n00 + (n10 - n00) * sx;
  const b = n01 + (n11 - n01) * sx;
  return a + (b - a) * sz;
}

/** Fractal value noise in [0, 1). */
export function fbm2(x: number, z: number, seed: number, octaves = 4): number {
  let amp = 0.5;
  let freq = 1;
  let sum = 0;
  let norm = 0;
  for (let i = 0; i < octaves; i++) {
    sum += valueNoise2(x * freq, z * freq, seed + i * 1013) * amp;
    norm += amp;
    amp *= 0.5;
    freq *= 2;
  }
  return sum / norm;
}
